/**
 * Resource Types Store
 *
 * Reads from and writes to public.planning_resource_type_overrides (migration 546).
 * Built-in types are defined in code; the DB stores only deviations (disabled
 * builtins and custom types). This keeps the table sparse while being
 * fully DB-backed per §5 build contract.
 *
 * §5 build contract: cross-user/cross-session state is DB-backed.
 */

import { create } from 'zustand';
import { getSupabaseClient } from '../../lib/supabaseClient';

export interface ResourceTypeDefinition {
  key: string;
  label: string;
  description?: string;
  builtin: boolean;
  enabled: boolean;
}

export const BUILTIN_RESOURCE_TYPES: ResourceTypeDefinition[] = [
  { key: 'engineering',  label: 'Engineering',        description: 'Software engineers, architects, and platform engineers', builtin: true, enabled: true },
  { key: 'design',       label: 'Design',             description: 'Product designers, UX researchers, and visual designers', builtin: true, enabled: true },
  { key: 'pm',           label: 'Product Management', description: 'Product managers and product owners', builtin: true, enabled: true },
  { key: 'data',         label: 'Data',               description: 'Data engineers, analysts, and ML/AI practitioners', builtin: true, enabled: true },
  { key: 'qa',           label: 'QA / Testing',       description: 'Quality assurance engineers and test specialists', builtin: true, enabled: true },
  { key: 'devops',       label: 'DevOps / Platform',  description: 'DevOps, SRE, and infrastructure engineers', builtin: true, enabled: true },
  { key: 'research',     label: 'Research',           description: 'UX research, market research, and user interviews', builtin: true, enabled: true },
  { key: 'marketing',    label: 'Marketing',          description: 'Marketing, content, and growth specialists', builtin: true, enabled: true },
  { key: 'sales',        label: 'Sales',              description: 'Sales, account management, and business development', builtin: true, enabled: true },
  { key: 'support',      label: 'Support',            description: 'Customer success, support, and enablement', builtin: true, enabled: true },
  { key: 'legal',        label: 'Legal',              description: 'Legal, compliance, and privacy specialists', builtin: true, enabled: true },
  { key: 'finance',      label: 'Finance',            description: 'Finance, FP&A, and procurement', builtin: true, enabled: true },
  { key: 'other',        label: 'Other',              description: 'Any other team or function not listed above', builtin: true, enabled: true },
];

interface ResourceTypesState {
  /** Override rows from DB: key → {enabled, label, description} */
  overrides: Record<string, { enabled: boolean; label: string; description?: string }>;
  /** Custom (non-builtin) types from DB */
  customTypes: ResourceTypeDefinition[];
  loading: boolean;
  error: string | null;

  load: (workspaceId: string) => Promise<void>;

  getAllTypes: () => ResourceTypeDefinition[];
  getEnabledTypes: () => ResourceTypeDefinition[];

  toggleBuiltin: (workspaceId: string, key: string, enabled: boolean) => Promise<void>;
  addCustomType: (workspaceId: string, input: Omit<ResourceTypeDefinition, 'builtin' | 'enabled'> & { enabled?: boolean }) => Promise<void>;
  updateCustomType: (workspaceId: string, key: string, patch: Partial<Pick<ResourceTypeDefinition, 'label' | 'description' | 'enabled'>>) => Promise<void>;
  deleteCustomType: (workspaceId: string, key: string) => Promise<void>;
}

export const useResourceTypesStore = create<ResourceTypesState>()((set, get) => ({
  overrides: {},
  customTypes: [],
  loading: false,
  error: null,

  load: async (workspaceId) => {
    if (!workspaceId) return;
    set({ loading: true, error: null });
    try {
      const { data, error } = await getSupabaseClient()
        .from('planning_resource_type_overrides')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('sort_order', { ascending: true });
      if (error) throw error;

      const rows = (data ?? []) as Array<{
        key: string; label: string; description?: string;
        builtin: boolean; enabled: boolean;
      }>;

      const overrides: ResourceTypesState['overrides'] = {};
      const customTypes: ResourceTypeDefinition[] = [];

      for (const row of rows) {
        if (row.builtin) {
          overrides[row.key] = { enabled: row.enabled, label: row.label, description: row.description };
        } else {
          customTypes.push({ key: row.key, label: row.label, description: row.description, builtin: false, enabled: row.enabled });
        }
      }

      set({ overrides, customTypes });
    } catch (err) {
      const msg = err instanceof Error ? err.message : (err as { message?: string })?.message ?? String(err);
      set({ error: msg });
    } finally {
      set({ loading: false });
    }
  },

  getAllTypes: () => {
    const { overrides, customTypes } = get();
    const builtins = BUILTIN_RESOURCE_TYPES.map((rt) => {
      const ov = overrides[rt.key];
      return ov ? { ...rt, enabled: ov.enabled, label: ov.label, description: ov.description ?? rt.description } : rt;
    });
    return [...builtins, ...customTypes];
  },

  getEnabledTypes: () => get().getAllTypes().filter((rt) => rt.enabled),

  toggleBuiltin: async (workspaceId, key, enabled) => {
    const bt = BUILTIN_RESOURCE_TYPES.find((r) => r.key === key);
    if (!bt) return;
    // Optimistic update
    set((s) => ({ overrides: { ...s.overrides, [key]: { ...s.overrides[key], enabled, label: bt.label } } }));
    const { error } = await getSupabaseClient()
      .from('planning_resource_type_overrides')
      .upsert(
        { workspace_id: workspaceId, key, label: bt.label, description: bt.description, builtin: true, enabled, updated_at: new Date().toISOString() },
        { onConflict: 'workspace_id,key' },
      );
    if (error) set({ error: error.message });
  },

  addCustomType: async (workspaceId, { key, label, description, enabled = true }) => {
    const existing = get().getAllTypes().find((rt) => rt.key === key);
    if (existing) throw new Error(`Resource type "${key}" already exists.`);
    const newType: ResourceTypeDefinition = { key, label, description, builtin: false, enabled };
    // Optimistic update
    set((s) => ({ customTypes: [...s.customTypes, newType] }));
    const { error } = await getSupabaseClient()
      .from('planning_resource_type_overrides')
      .insert({ workspace_id: workspaceId, key, label, description, builtin: false, enabled });
    if (error) {
      // Roll back
      set((s) => ({ customTypes: s.customTypes.filter((rt) => rt.key !== key), error: error.message }));
      throw new Error(error.message);
    }
  },

  updateCustomType: async (workspaceId, key, patch) => {
    set((s) => ({
      customTypes: s.customTypes.map((rt) => rt.key === key ? { ...rt, ...patch } : rt),
    }));
    const { error } = await getSupabaseClient()
      .from('planning_resource_type_overrides')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('workspace_id', workspaceId)
      .eq('key', key);
    if (error) set({ error: error.message });
  },

  deleteCustomType: async (workspaceId, key) => {
    set((s) => ({ customTypes: s.customTypes.filter((rt) => rt.key !== key) }));
    const { error } = await getSupabaseClient()
      .from('planning_resource_type_overrides')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('key', key);
    if (error) set({ error: error.message });
  },
}));

/** A team's name for its key ("pm" → "Product Management"); the key itself when it is not a known team. */
export function resourceTypeLabel(key: string): string {
  return useResourceTypesStore.getState().getAllTypes().find((rt) => rt.key === key)?.label ?? key;
}

export function getEnabledResourceTypeKeys(): string[] {
  return useResourceTypesStore.getState().getEnabledTypes().map((rt) => rt.key);
}
